import { useState } from 'react';
import { Typography, OptionsMenu, Spinner } from '@scale/scaleui';
import { CorpDialogueEntry } from '../demo/CorpDialogueEntry';
import API from '@scale/frontend-lib/dashboard-api';
import { SaveDialogueModal } from '../demo/SaveDialogueModal';
import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
  SavedCorpDialogue,
} from '@scale/shared/types/chat';
import { useQuery } from 'react-query'; // NOTE: @lajos We should use @tanstack/react-query instead. We can't since the top provider is from the old package.

export type TProps = {
  /**
   * Config to save - used only if not readOnly mode.
   */
  config: MultiTurnInteractionConfiguration;

  /**
   * Responses to save - used only if not readOnly mode.
   */
  responses: IndexedInteractionStepResponse[];

  /**
   * Function called on config selection.
   */
  setConfig: (config: MultiTurnInteractionConfiguration) => void;

  /**
   * Function called on config selection.
   */
  setResponses: (responses: IndexedInteractionStepResponse[]) => void;

  /**
   * Title of the component.
   */
  title?: string;

  /**
   * Wheter to list just starred or all configs.
   */
  onlyStarred?: boolean;

  /**
   * Disables all actions - makes the component a configs picker.
   */
  readOnly?: boolean;
};

const sortDialogues = (a: SavedCorpDialogue, b: SavedCorpDialogue) => {
  const aStarred = a.tags.includes('starred') ? 0 : 1;
  const bStarred = b.tags.includes('starred') ? 0 : 1;
  return aStarred - bStarred;
};

const ConfigurationPresets = ({
  config,
  responses,
  setConfig,
  setResponses,
  title,
  onlyStarred = false,
  readOnly = false,
}: TProps) => {
  const [saveDialogueOpen, setSaveDialogueOpen] = useState<boolean>(false);
  const [showAllSavedDialogues, setShowAllSavedDialogues] = useState<boolean>(false);

  const {
    data: savedDialogues,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['fetchCorpDialogues'],
    queryFn: ({ signal }) => API.genai.fetchCorpDialogues({ signal }),
    select: data => data.dialogues.sort(sortDialogues),
    initialData: () => ({ dialogues: [], success: true }),
  });

  const starredDialogues = savedDialogues?.filter(d => d.tags.includes('starred')) || [];

  return (
    <>
      <div
        className="flex items-center gap-1 bg-transparent hover:bg-neutral-20 cursor-pointer"
        onClick={() => {}}
      >
        {title && <Typography.Subtitle1>Saved</Typography.Subtitle1>}

        {!readOnly && (
          <OptionsMenu
            buttonIcon="angle-down"
            className="flex items-center"
            menuItems={[
              {
                icon: ['far', 'save'],
                id: 'save-current',
                label: 'Save config + responses',
                onClick: () => setSaveDialogueOpen(true),
              },
            ]}
          />
        )}
      </div>

      {isFetching ? (
        <div className="w-full flex justify-center pt-4">
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          {(showAllSavedDialogues ? savedDialogues ?? [] : starredDialogues).map(dialogue => (
            <CorpDialogueEntry
              key={dialogue._id}
              dialogue={dialogue}
              setConfig={setConfig}
              setResponses={setResponses}
              onUpdate={refetch}
              readOnly={readOnly}
            />
          ))}

          {!onlyStarred && !showAllSavedDialogues && (
            <div
              className="text-primary-500 text-xs cursor-pointer"
              onClick={() => setShowAllSavedDialogues(true)}
            >
              Show all saved dialogues
            </div>
          )}
        </>
      )}

      <SaveDialogueModal
        open={saveDialogueOpen}
        onClose={() => {
          setSaveDialogueOpen(false);
          refetch();
        }}
        config={config}
        responses={responses}
      />
    </>
  );
};

export default ConfigurationPresets;
